import {
  css,
  Link,
  styled,
  Text,
  useContextPath,
  useIsMobile,
  useTranslation,
} from '@obvio/app'
import { SvgArrowSimple, SvgCalendar } from '@obvio/svg'
import { useAuth, useCategories, useUi } from '@obvio/template'
import { Avatar, Collapse, Stack } from '@obvio/ui'
import { AnimatePresence, motion, useAnimationControls } from 'framer-motion'
import { useCallback, useEffect, useRef, useState } from 'react'

import {
  CheckAll,
  EventsPanel,
  GroupElement,
  GroupTitle,
  GroupToggle,
  MenuItem,
  Menu,
  MotionMenu,
  StackList,
  StyledDrawer,
} from './components'
import { links } from './consts'
import { ExpandMorePanel } from './NavExpandMorePanel'
import { NavLangsAndSocials } from './NavLangsAndSocials'
import { Events } from '../Renderer/RendererVariants/Events/Events'

import type { LinkGroup } from './consts'
import type { ReactElement } from 'react'

const Backdrop = styled.div`
  position: fixed;
  inset: 0px;
  z-index: 1;
`

const GroupStack = styled(Stack)`
  margin-top: ${(theme) => theme.spacing.small};
  padding-bottom: 0.1rem;
`

const MotionDrawer = motion(StyledDrawer, { forwardMotionProps: true })
const MotionBackdrop = motion(Backdrop)
const MotionGroupTitle = motion(GroupTitle)
const MotionGroupElement = motion(GroupElement)

type GroupProps = {
  group: LinkGroup
}

function LineAnimation() {
  return (
    <motion.div
      transition={{
        type: 'tween',
        duration: 0.25,
      }}
      initial={{
        position: 'absolute',
        left: '0%',
        height: '1px',
        background: '#31352C',
      }}
      variants={{
        hovering: {
          width: '100%',
        },
      }}
    />
  )
}

function Group({ group }: GroupProps) {
  const { locale } = useTranslation()

  return (
    <GroupStack list kind="vertical" spacing="medium">
      {group.links?.map((link) => (
        <MotionGroupElement whileHover="hovering" key={link.href}>
          <Link href={link.href}>{link.title[locale]}</Link>
          <LineAnimation />
        </MotionGroupElement>
      ))}
    </GroupStack>
  )
}

const contentStyles = css`
  background: transparent;
  z-index: 50;
`

const Arrow = styled(SvgArrowSimple)`
  transform: rotate(90deg);
  margin-left: ${(theme) => theme.spacing.small};
  height: 1.25rem !important;
  width: 1.25rem !important;
`

const ExpandMorePanelBlack = styled(ExpandMorePanel)`
  color: ${(theme) => theme.colors.secondary};
`

const UserAvatar = styled(Avatar)`
  background: ${(theme) => theme.colors.secondary};
  > svg {
    height: 1rem !important;
    width: 1rem !important;
  }
`

const LoginWrap = styled(Stack)`
  align-items: center;
`

export function NavMenu(): ReactElement | null {
  const { categories } = useCategories()
  const { locale, t } = useTranslation()
  const initial = useRef(true)
  const animate = useAnimationControls()
  const [eventsOpened, setEventsOpened] = useState(false)
  const [openGroup, setOpenGroup] = useState<number | null>(null)
  const isMobile = useIsMobile()
  const [{ menuPanelOpen }, dispatch] = useUi('menuPanelOpen')
  //  const scrollLockRef = useScrollLock(Boolean(menuPanelOpen))

  const toggleEventsPanel = useCallback(
    () => setEventsOpened((current) => !current),
    [],
  )
  const closePanels = useCallback(
    () => dispatch('CLOSE_ALL_PANELS'),
    [dispatch],
  )

  const panelDirection = isMobile ? 'y' : 'x'

  const { user } = useAuth()

  const userPanelPath = useContextPath('ecommerce.userPanel')
  const signInPath = useContextPath('signIn')
  const avatarPath = user ? userPanelPath : signInPath

  useEffect(() => {
    if (initial.current) {
      void animate.start('drawerInitial')
      initial.current = false
    } else if (menuPanelOpen) {
      void animate.start('drawerAnimate')
    } else {
      // eslint-disable-next-line promise/prefer-await-to-then
      void animate.start('drawerExit').then(() => {
        void animate.start('drawerExited')
      })
    }
    initial.current = false
  }, [animate, menuPanelOpen, panelDirection])

  const customBackdrop = useCallback(
    () => (
      <MotionBackdrop
        onClick={closePanels}
        variants={{
          drawerAnimate: {
            background: 'rgba(0,0,0,0.55)',
          },
          drawerExit: {
            background: 'rgba(0,0,0,0)',
          },
        }}
        transition={{
          duration: 0.75,
        }}
      />
    ),
    [closePanels],
  )

  return (
    <MotionDrawer
      contentStyle={contentStyles}
      noMaxWidth
      visible={Boolean(menuPanelOpen)}
      handleClickOutside={closePanels}
      position="left"
      animate={animate}
      // TODO: FIXME, it's not working at all tho a simple case on codesandbox works
      customBackdrop={customBackdrop}
      variants={{
        drawerInitial: {
          zIndex: -1,
          position: 'fixed',
        },
        drawerAnimate: {
          zIndex: 50,
        },
        drawerExited: {
          zIndex: -1,
        },
      }}
    >
      <MotionMenu
        initial={{
          [panelDirection]: panelDirection === 'x' ? '-44rem' : '-100%',
        }}
        variants={{
          drawerInitial: {
            [panelDirection]: panelDirection === 'x' ? '-44rem' : '-100%',
          },
          drawerAnimate: {
            [panelDirection]: panelDirection === 'x' ? '0rem' : '0%',
            [panelDirection === 'x' ? 'y' : 'x']: 0,
            opacity: '1',
          },
          drawerExit: {
            [panelDirection]: panelDirection === 'x' ? '-44rem' : '-100%',
            [panelDirection === 'x' ? 'y' : 'x']: 0,
          },
          drawerExited: {
            opacity: '0',
          },
        }}
        transition={{
          type: 'tween',
          duration: 0.75,
        }}
        key="menu"
      >
        <Menu>
          <NavLangsAndSocials />
          <div style={{ overflow: 'auto', height: '100%' }}>
            <StackList list kind="vertical" spacing="2.65rem">
              {links.map((group, index) => {
                const subLinks: LinkGroup['links'] =
                  group.href === '/sklep'
                    ? [
                        {
                          title: { pl: 'Strona główna', en: 'Main page' },
                          href: '/sklep',
                        },
                        ...(categories?.map((category) => ({
                          href: `/sklep/kategoria/${category.slug}`,
                          title: { pl: category.title, en: category.title },
                        })) ?? []),
                      ]
                    : group.links

                if (!subLinks && 'href' in group) {
                  return (
                    <MenuItem key={group.title[locale]}>
                      <MotionGroupTitle
                        whileHover="hovering"
                        onClick={() => dispatch('CLOSE_ALL_PANELS')}
                      >
                        <Link href={group.href}>{group.title[locale]}</Link>
                        <LineAnimation />
                      </MotionGroupTitle>
                    </MenuItem>
                  )
                }

                const open = openGroup === index
                return (
                  <MenuItem key={group.title[locale]}>
                    <GroupToggle
                      type="button"
                      aria-expanded={open}
                      onClick={() => setOpenGroup(open ? null : index)}
                    >
                      <MotionGroupTitle
                        $open={open}
                        whileHover={{ x: open ? 0 : 5 }}
                      >
                        {group.title[locale]}
                        {open && <Arrow />}
                      </MotionGroupTitle>
                    </GroupToggle>
                    <Collapse open={open}>
                      <Group
                        group={
                          { ...group, links: subLinks } as unknown as LinkGroup
                        }
                      />
                    </Collapse>
                  </MenuItem>
                )
              })}
              <MenuItem>
                <Link href={avatarPath} onClick={closePanels}>
                  <LoginWrap>
                    <UserAvatar />
                    <Text tag="span">
                      {user ? 'PANEL UŻYTKOWNIKA' : 'ZALOGUJ SIĘ'}
                    </Text>
                  </LoginWrap>
                </Link>
              </MenuItem>
            </StackList>
          </div>
        </Menu>
        <ExpandMorePanelBlack
          href="/atrakcje"
          title={
            <Link href="/atrakcje" aria-label={t('nav.events')}>
              <SvgCalendar />
            </Link>
          }
          text="DZIEJE SIĘ W 511"
          onClick={toggleEventsPanel}
          opened={eventsOpened}
          rotated={false}
        />
        <AnimatePresence>
          {eventsOpened && (
            <motion.div
              initial={{ [panelDirection]: '-100%', width: '100vw' }}
              animate={{
                [panelDirection]: 0,
                transitionEnd: {
                  [panelDirection]: 0,
                },
              }}
              exit={{ [panelDirection]: '-100%' }}
              transition={{
                type: 'tween',
                duration: 1,
              }}
              variants={{
                drawerExit: {
                  [panelDirection]: '-125%',
                  transition: {
                    duration: 0.75,
                  },
                },
              }}
            >
              <EventsPanel templateRows="2rem 1fr">
                <CheckAll href="/atrakcje">
                  Sprawdź wszystkie wydarzenia
                </CheckAll>
                <Events kind="header" />
              </EventsPanel>
            </motion.div>
          )}
        </AnimatePresence>
      </MotionMenu>
    </MotionDrawer>
  )
}
